// translations.js - Centralized translation file

// Shared English fallback keys — spread into any locale that hasn't been fully translated yet.
const _ek = {
    noCarsAdded: 'No Cars Added',
    tapToAddCar: 'Tap here to add your first car',
    tapToSetMileage: 'Tap to set mileage',
    todaySuffix: 'today',
    carLocked: 'Car Locked',
    unlockProMsg: 'Upgrade to CarTrack+ to unlock unlimited car profiles.',
    unlockNow: 'Unlock Now',
    deleteThisCar: 'Delete this car',
    cardAppearance: 'Card Appearance',
    customBackground: 'Custom Background',
    uploadCarPhoto: 'Upload a photo of your car',
    removeBackground: 'Remove Background',
    restoreDefault: 'Restore default appearance',
    verticalAlignment: 'Vertical Alignment',
    posTop: 'Top', posCenter: 'Center', posBottom: 'Bottom',
    visuals: 'Visuals', visible: 'Visible', hidden: 'Hidden', done: 'Done',
    bestValue: 'Best Value',
    premiumFeatures: 'Premium Features:',
    monthly: 'Monthly', yearly: 'Yearly',
    proFeatures: [
        { icon: '📍', text: 'Automatic GPS mileage tracking' },
        { icon: '🚗', text: 'Unlimited cars' },
        { icon: '🔔', text: 'Smart service-due notifications' },
        { icon: '🚫', text: 'Ad-free experience' },
        { icon: '🕐', text: 'Smart maintenance grid' },
        { icon: '💳', text: 'Customize your car card with images' },
    ],
    unlockPremiumMsg: 'Unlock premium features and take full control of your car tracking experience.',
    deleteCarTitle: (name) => `Delete ${name}?`,
    deleteCarBody: 'This will permanently delete the car and all its expenses. This cannot be undone.',
    delete: 'Delete',
    recentChanges: 'Recent changes',
    noRecentChanges: 'No recent changes',
    revenueStats: 'Revenue Stats',
    revenueStatsDesc: 'View your service revenue analytics',
    myProfile: 'My Profile',
    saveChanges: 'Save Changes',
    yourName: 'Your name…',
    maintenanceGuide: 'Maintenance Guide',
    maintenanceGuideBody: (y, r) => `${y} on the maintenance grid means recommended change and ${r} means overdue.`,
    maintenanceGuideBodyMid: 'on the maintenance grid means recommended change and',
    maintenanceGuideBodyEnd: 'means overdue.',
    yellow: 'Yellow', red: 'red', gotIt: 'Got it',
    promoTitle: 'Tired of manual entry?',
    promoBody: 'Let CarTrack automatically log your miles in the background!',
    noThanks: "No thanks, I'll do it manually",
    enableBgLocation: 'Enable Background Location',
    bgLocationBody: 'To track your miles automatically — even when the app is minimized — set location access to "Always allow".',
    bgLocationPath: '📱 Settings → Apps → CarTrack → Permissions → Location → Always allow',
    gotItUpdateNow: "Got it, I'll update it now",
    skipForNow: 'Skip for now',
    overdueByDays: (n) => `Overdue by ${n}d`,
    daysLeft: (n) => `${n}d left`,
    monthsLeft: (n) => `${n}mo left`,
    overdueByDist: (n, u) => `Overdue by ${n}${u}`,
    distLeft: (n, u) => `${n}${u} left`,
    noDistData: (u) => `-- ${u} left`,
    conditionLabel: 'Condition — based on mileage & time',
    adjustInterval: 'Adjust interval',
    serviceInterval: 'Service interval',
    zoneExcellent: 'Excellent',
    zoneRecommended: 'Recommended',
    zoneOverdue: 'Overdue',
    addFirstExpensePrompt: 'Add your first expense using the + in the bottom navigation menu.',
};

export const translations = {
    en: {
        appName: 'CarTrack',
        dashboard: 'Dashboard',
        welcomeBack: 'Welcome back. Here is your expense overview.',
        changeCar: 'Change Car',
        editCarName: 'Edit car name',
        expensesBreakdown: 'Expenses Breakdown',
        noExpensesData: 'No expenses data available for statistics.',
        expenseLog: 'Expense Log',
        manageExpenses: 'Manage your car related costs',
        totalSpent: 'Total Spent',
        filterByCategory: 'Filter by Category',
        addNewExpense: 'Add New Expense',
        noExpenses: 'No expenses recorded yet.',
        addFirstExpensePrompt: 'Add your first expense using the + in the bottom navigation menu.',
        searchExpenses: 'Search expenses…',
        newExpense: 'New Expense',
        title: 'Title',
        amount: 'Amount',
        date: 'Date',
        category: 'Category',
        filterType: 'Filter Type',
        expiryDate: 'Valid Until',
        cancel: 'Cancel',
        saveExpense: 'Save Expense',
        categories: {
            brakes: 'Brakes',
            brakePads: 'Brake Pads',
            brakeRotors: 'Brake Rotors',



            all: 'All Categories',
            oilChange: 'Oil Change',
            filter: 'Filter',
            oilFilter: 'Oil Filter',
            fuelFilter: 'Fuel Filter',
            cabinFilter: 'Cabin Filter',
            transmissionFilter: 'Transmission Filter',
            airFilter: 'Air Filter',
            carInspection: 'Car Inspection',
            maintenance: 'Maintenance',
            insurance: 'Insurance',
            repairs: 'Repairs',
            other: 'Other'
        },
        settings: 'Settings',
        preferences: 'Preferences',
        currency: 'Currency',
        language: 'Language',
        guestUser: 'Guest User',
        email: 'guest@cartrack.app',
        manageAccount: 'Manage Account',
        signOut: 'Sign Out',
        close: 'Close',
        currencies: { usd: 'US Dollar', eur: 'Euro', gbp: 'British Pound' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Front / Back',
        front: 'Front',
        back: 'Back',
        frontAndBack: 'Front & Back',
        placeholders: { expenseTitle: 'e.g. Engine Oil Change', amount: '0.00' },
        // CarCard
        noCarsAdded: 'No Cars Added',
        tapToAddCar: 'Tap here to add your first car',
        tapToSetMileage: 'Tap to set mileage',
        todaySuffix: 'today',
        carLocked: 'Car Locked',
        unlockProMsg: 'Upgrade to CarTrack+ to unlock unlimited car profiles.',
        unlockNow: 'Unlock Now',
        deleteThisCar: 'Delete this car',
        cardAppearance: 'Card Appearance',
        customBackground: 'Custom Background',
        uploadCarPhoto: 'Upload a photo of your car',
        removeBackground: 'Remove Background',
        restoreDefault: 'Restore default appearance',
        verticalAlignment: 'Vertical Alignment',
        posTop: 'Top',
        posCenter: 'Center',
        posBottom: 'Bottom',
        visuals: 'Visuals',
        visible: 'Visible',
        hidden: 'Hidden',
        done: 'Done',
        bestValue: 'Best Value',
        premiumFeatures: 'Premium Features:',
        monthly: 'Monthly',
        yearly: 'Yearly',
        proFeatures: [
            { icon: '📍', text: 'Automatic GPS mileage tracking' },
            { icon: '🚗', text: 'Unlimited cars' },
            { icon: '🔔', text: 'Smart service-due notifications' },
            { icon: '🚫', text: 'Ad-free experience' },
            { icon: '🕐', text: 'Smart maintenance grid' },
            { icon: '💳', text: 'Customize your car card with images' },
        ],
        unlockPremiumMsg: 'Unlock premium features and take full control of your car tracking experience.',
        deleteCarTitle: (name) => `Delete ${name}?`,
        deleteCarBody: 'This will permanently delete the car and all its expenses. This cannot be undone.',
        delete: 'Delete',
        // App dashboard
        recentChanges: 'Recent changes',
        noRecentChanges: 'No recent changes',
        revenueStats: 'Revenue Stats',
        revenueStatsDesc: 'View your service revenue analytics',
        myProfile: 'My Profile',
        saveChanges: 'Save Changes',
        yourName: 'Your name…',
        // Maintenance guide popup
        maintenanceGuide: 'Maintenance Guide',
        maintenanceGuideBody: (y, r) => `${y} on the maintenance grid means recommended change and ${r} means overdue.`,
        maintenanceGuideBodyMid: 'on the maintenance grid means recommended change and',
        maintenanceGuideBodyEnd: 'means overdue.',
        yellow: 'Yellow',
        red: 'red',
        gotIt: 'Got it',
        // Promo popup
        promoTitle: 'Tired of manual entry?',
        promoBody: 'Let CarTrack automatically log your miles in the background!',
        noThanks: "No thanks, I'll do it manually",
        // Background location prompt
        enableBgLocation: 'Enable Background Location',
        bgLocationBody: 'To track your miles automatically — even when the app is minimized — set location access to "Always allow".',
        bgLocationPath: '📱 Settings → Apps → CarTrack → Permissions → Location → Always allow',
        gotItUpdateNow: "Got it, I'll update it now",
        skipForNow: 'Skip for now',
        // MaintenanceGrid
        overdueByDays: (n) => `Overdue by ${n}d`,
        daysLeft: (n) => `${n}d left`,
        monthsLeft: (n) => `${n}mo left`,
        overdueByDist: (n, u) => `Overdue by ${n}${u}`,
        distLeft: (n, u) => `${n}${u} left`,
        noDistData: (u) => `-- ${u} left`,
        conditionLabel: 'Condition — based on mileage & time',
        adjustInterval: 'Adjust interval',
        serviceInterval: 'Service interval',
        zoneExcellent: 'Excellent',
        zoneRecommended: 'Recommended',
        zoneOverdue: 'Overdue'
    },

    bg: {
        appName: 'CarTrack',
        dashboard: 'Табло',
        welcomeBack: 'Добре дошли обратно. Ето вашия преглед на разходите.',
        changeCar: 'Смяна на кола',
        editCarName: 'Редактиране на името',
        expensesBreakdown: 'Разбивка на разходите',
        noExpensesData: 'Няма данни за разходи за статистика.',
        expenseLog: 'Дневник на разходите',
        manageExpenses: 'Управлявайте разходите за вашата кола',
        totalSpent: 'Общо изразходвани',
        filterByCategory: 'Филтриране по категория',
        addNewExpense: 'Добави нов разход',
        noExpenses: 'Все още няма записани разходи.',
        addFirstExpensePrompt: 'Добавете първия си разход чрез + в долното навигационно меню.',
        searchExpenses: 'Търси разходи…',
        newExpense: 'Нов разход',
        title: 'Заглавие',
        amount: 'Сума',
        date: 'Дата',
        category: 'Категория',
        filterType: 'Тип филтър',
        expiryDate: 'Валидно до',
        cancel: 'Отказ',
        saveExpense: 'Запази разход',
        categories: {
            brakes: 'Спирачки',
            brakePads: 'Накладки',
            brakeRotors: 'Дискове',



            all: 'Всички категории',
            oilChange: 'Смяна на масло',
            filter: 'Филтър',
            oilFilter: 'Маслен филтър',
            fuelFilter: 'Горивен филтър',
            cabinFilter: 'Филтър на купе',
            transmissionFilter: 'Филтър на скоростна кутия',
            airFilter: 'Въздушен филтър',
            carInspection: 'Годишен технически преглед',
            maintenance: 'Поддръжка',
            insurance: 'Застраховка',
            repairs: 'Ремонти',
            other: 'Друго'
        },
        settings: 'Настройки',
        preferences: 'Предпочитания',
        currency: 'Валута',
        language: 'Език',
        guestUser: 'Гост потребител',
        email: 'guest@cartrack.app',
        manageAccount: 'Управление на акаунта',
        signOut: 'Излизане',
        close: 'Затвори',
        currencies: { usd: 'Американски долар', eur: 'Евро', gbp: 'Британска лира' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Предни / Задни',
        front: 'Предни',
        back: 'Задни',
        frontAndBack: 'Предни и Задни',
        placeholders: { expenseTitle: 'напр. Смяна на моторно масло', amount: '0.00' },
        noCarsAdded: 'Няма добавени коли',
        tapToAddCar: 'Натиснете тук, за да добавите първата си кола',
        tapToSetMileage: 'Натиснете за пробег',
        todaySuffix: 'днес',
        carLocked: 'Колата е заключена',
        unlockProMsg: 'Надградете до CarTrack+, за да отключите неограничени профили.',
        unlockNow: 'Отключи сега',
        deleteThisCar: 'Изтрий тази кола',
        cardAppearance: 'Изглед на картата',
        customBackground: 'Персонализиран фон',
        uploadCarPhoto: 'Качете снимка на колата',
        removeBackground: 'Премахни фон',
        restoreDefault: 'Върни стандартния изглед',
        verticalAlignment: 'Вертикално подравняване',
        posTop: 'Горе',
        posCenter: 'Център',
        posBottom: 'Долу',
        visuals: 'Визуализация',
        visible: 'Видимо',
        hidden: 'Скрито',
        done: 'Готово',
        bestValue: 'Най-изгодно',
        premiumFeatures: 'Премиум функции:',
        monthly: 'Месечно',
        yearly: 'Годишно',
        proFeatures: [
            { icon: '📍', text: 'Автоматично проследяване на километри' },
            { icon: '🚗', text: 'Неограничени автомобили' },
            { icon: '🔔', text: 'Известия за предстояща поддръжка' },
            { icon: '🚫', text: 'Изживяване без реклами' },
            { icon: '🕐', text: 'Интелигентна мрежа за поддръжка' },
            { icon: '💳', text: 'Персонализирайте картата с изображения' },
        ],
        unlockPremiumMsg: 'Отключете премиум функции и поемете пълен контрол.',
        deleteCarTitle: (name) => `Изтриване на ${name}?`,
        deleteCarBody: 'Това ще изтрие колата и всичките й разходи завинаги. Действието е необратимо.',
        delete: 'Изтрий',
        recentChanges: 'Последни промени',
        noRecentChanges: 'Няма скорошни промени',
        revenueStats: 'Статистика на приходите',
        revenueStatsDesc: 'Прегледайте анализите на приходите от услуги',
        myProfile: 'Моят профил',
        saveChanges: 'Запази промените',
        yourName: 'Вашето име…',
        maintenanceGuide: 'Ръководство за поддръжка',
        maintenanceGuideBody: (y, r) => `${y} в мрежата за поддръжка означава препоръчителна смяна, а ${r} означава просрочено.`,
        maintenanceGuideBodyMid: 'в мрежата за поддръжка означава препоръчителна смяна, а',
        maintenanceGuideBodyEnd: 'означава просрочено.',
        yellow: 'Жълто',
        red: 'червено',
        gotIt: 'Разбрах',
        promoTitle: 'Уморени от ръчно въвеждане?',
        promoBody: 'Нека CarTrack автоматично записва километрите ви!',
        noThanks: 'Не, благодаря, ще го правя ръчно',
        enableBgLocation: 'Разреши фонова локация',
        bgLocationBody: 'За автоматично проследяване задайте достъп до локация на "Винаги".',
        bgLocationPath: '📱 Настройки → Приложения → CarTrack → Разрешения → Локация → Винаги',
        gotItUpdateNow: 'Разбрах, ще го актуализирам',
        skipForNow: 'Пропусни засега',
        overdueByDays: (n) => `Просрочено с ${n}д`,
        daysLeft: (n) => `${n}д остават`,
        monthsLeft: (n) => `${n}м остават`,
        overdueByDist: (n, u) => `Просрочено с ${n}${u}`,
        distLeft: (n, u) => `${n}${u} остават`,
        noDistData: (u) => `-- ${u} остават`,
        conditionLabel: 'Състояние — по пробег и дата',
        adjustInterval: 'Коригирай интервал',
        serviceInterval: 'Сервизен интервал',
        zoneExcellent: 'Отлично',
        zoneRecommended: 'Препоръчително',
        zoneOverdue: 'Просрочено'
    },

    zh: {
        appName: 'CarTrack',
        dashboard: '仪表盘',
        welcomeBack: '欢迎回来。以下是您的支出概览。',
        changeCar: '切换车辆',
        editCarName: '编辑车辆名称',
        expensesBreakdown: '支出明细',
        noExpensesData: '暂无统计数据。',
        expenseLog: '支出记录',
        manageExpenses: '管理您的汽车相关费用',
        totalSpent: '总支出',
        filterByCategory: '按类别筛选',
        addNewExpense: '添加新支出',
        noExpenses: '尚未记录任何支出。',
        newExpense: '新支出',
        title: '标题',
        amount: '金额',
        date: '日期',
        category: '类别',
        filterType: '滤芯类型',
        expiryDate: '有效期至',
        cancel: '取消',
        saveExpense: '保存支出',
        categories: {
            brakes: '刹车',
            brakePads: '刹车片',
            brakeRotors: '刹车盘',



            all: '所有类别',
            oilChange: '换机油',
            filter: '滤芯',
            oilFilter: '机油滤芯',
            fuelFilter: '燃油滤芯',
            cabinFilter: '空调滤芯',
            transmissionFilter: '变速箱滤芯',
            airFilter: '空气滤芯',
            carInspection: '车辆检验',
            maintenance: '保养',
            insurance: '保险',
            repairs: '维修',
            other: '其他'
        },
        settings: '设置',
        preferences: '偏好设置',
        currency: '货币',
        language: '语言',
        guestUser: '访客用户',
        email: 'guest@cartrack.app',
        manageAccount: '管理账户',
        signOut: '退出登录',
        close: '关闭',
        currencies: { usd: '美元', eur: '欧元', gbp: '英镑' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: '前 / 后',
        front: '前',
        back: '后',
        frontAndBack: '前和后',
        placeholders: { expenseTitle: '如：更换发动机机油', amount: '0.00' },
        ..._ek,
    },

    hi: {
        appName: 'CarTrack',
        dashboard: 'डैशबोर्ड',
        welcomeBack: 'वापस स्वागत है। यहाँ आपका खर्च विवरण है।',
        changeCar: 'कार बदलें',
        editCarName: 'कार का नाम संपादित करें',
        expensesBreakdown: 'खर्च विवरण',
        noExpensesData: 'सांख्यिकी के लिए कोई खर्च डेटा उपलब्ध नहीं।',
        expenseLog: 'खर्च लॉग',
        manageExpenses: 'अपनी कार से संबंधित लागत प्रबंधित करें',
        totalSpent: 'कुल खर्च',
        filterByCategory: 'श्रेणी के अनुसार फ़िल्टर करें',
        addNewExpense: 'नया खर्च जोड़ें',
        noExpenses: 'अभी तक कोई खर्च दर्ज नहीं।',
        newExpense: 'नया खर्च',
        title: 'शीर्षक',
        amount: 'राशि',
        date: 'तारीख',
        category: 'श्रेणी',
        filterType: 'फ़िल्टर प्रकार',
        expiryDate: 'वैध तक',
        cancel: 'रद्द करें',
        saveExpense: 'खर्च सहेजें',
        categories: {
            brakes: 'ब्रेक',
            brakePads: 'ब्रेक पैड',
            brakeRotors: 'ब्रेक रोटर्स',



            all: 'सभी श्रेणियाँ',
            oilChange: 'तेल परिवर्तन',
            filter: 'फ़िल्टर',
            oilFilter: 'तेल फ़िल्टर',
            fuelFilter: 'ईंधन फ़िल्टर',
            cabinFilter: 'केबिन फ़िल्टर',
            transmissionFilter: 'ट्रांसमिशन फ़िल्टर',
            airFilter: 'वायु फ़िल्टर',
            carInspection: 'कार निरीक्षण',
            maintenance: 'रखरखाव',
            insurance: 'बीमा',
            repairs: 'मरम्मत',
            other: 'अन्य'
        },
        settings: 'सेटिंग्स',
        preferences: 'प्राथमिकताएँ',
        currency: 'मुद्रा',
        language: 'भाषा',
        guestUser: 'अतिथि उपयोगकर्ता',
        email: 'guest@cartrack.app',
        manageAccount: 'खाता प्रबंधित करें',
        signOut: 'साइन आउट',
        close: 'बंद करें',
        currencies: { usd: 'अमेरिकी डॉलर', eur: 'यूरो', gbp: 'ब्रिटिश पाउंड' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'आगे / पीछे',
        front: 'आगे',
        back: 'पीछे',
        frontAndBack: 'आगे और पीछे',
        placeholders: { expenseTitle: 'जैसे: इंजन ऑयल बदलें', amount: '0.00' },
        ..._ek,
    },

    es: {
        appName: 'CarTrack',
        dashboard: 'Panel',
        welcomeBack: 'Bienvenido de nuevo. Aquí está su resumen de gastos.',
        changeCar: 'Cambiar coche',
        editCarName: 'Editar nombre del coche',
        expensesBreakdown: 'Desglose de gastos',
        noExpensesData: 'No hay datos de gastos disponibles para estadísticas.',
        expenseLog: 'Registro de gastos',
        manageExpenses: 'Gestiona los costes relacionados con tu coche',
        totalSpent: 'Total gastado',
        filterByCategory: 'Filtrar por categoría',
        addNewExpense: 'Añadir nuevo gasto',
        noExpenses: 'No hay gastos registrados aún.',
        newExpense: 'Nuevo gasto',
        title: 'Título',
        amount: 'Importe',
        date: 'Fecha',
        category: 'Categoría',
        filterType: 'Tipo de filtro',
        expiryDate: 'Válido hasta',
        cancel: 'Cancelar',
        saveExpense: 'Guardar gasto',
        categories: {
            brakes: 'Frenos',
            brakePads: 'Pastillas de freno',
            brakeRotors: 'Discos de freno',


            all: 'Todas las categorías',
            oilChange: 'Cambio de aceite',
            filter: 'Filtro',
            oilFilter: 'Filtro de aceite',
            fuelFilter: 'Filtro de combustible',
            cabinFilter: 'Filtro de habitáculo',
            transmissionFilter: 'Filtro de transmisión',
            airFilter: 'Filtro de aire',
            carInspection: 'Inspección del vehículo',
            maintenance: 'Mantenimiento',
            insurance: 'Seguro',
            repairs: 'Reparaciones',
            other: 'Otro'
        },
        settings: 'Configuración',
        preferences: 'Preferencias',
        currency: 'Moneda',
        language: 'Idioma',
        guestUser: 'Usuario invitado',
        email: 'guest@cartrack.app',
        manageAccount: 'Gestionar cuenta',
        signOut: 'Cerrar sesión',
        close: 'Cerrar',
        currencies: { usd: 'Dólar estadounidense', eur: 'Euro', gbp: 'Libra esterlina' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Delantero / Trasero',
        front: 'Delantero',
        back: 'Trasero',
        frontAndBack: 'Delantero y Trasero',
        placeholders: { expenseTitle: 'p.ej. Cambio de aceite del motor', amount: '0.00' },
        ..._ek,
    },

    fr: {
        appName: 'CarTrack',
        dashboard: 'Tableau de bord',
        welcomeBack: 'Bon retour. Voici votre aperçu des dépenses.',
        changeCar: 'Changer de voiture',
        editCarName: 'Modifier le nom de la voiture',
        expensesBreakdown: 'Détail des dépenses',
        noExpensesData: 'Aucune donnée de dépenses disponible pour les statistiques.',
        expenseLog: 'Journal des dépenses',
        manageExpenses: 'Gérez vos coûts liés à votre voiture',
        totalSpent: 'Total dépensé',
        filterByCategory: 'Filtrer par catégorie',
        addNewExpense: 'Ajouter une dépense',
        noExpenses: 'Aucune dépense enregistrée pour l\'instant.',
        newExpense: 'Nouvelle dépense',
        title: 'Titre',
        amount: 'Montant',
        date: 'Date',
        category: 'Catégorie',
        filterType: 'Type de filtre',
        expiryDate: 'Valable jusqu\'au',
        cancel: 'Annuler',
        saveExpense: 'Enregistrer la dépense',
        categories: {
            brakes: 'Freins',
            brakePads: 'Plaquettes de frein',
            brakeRotors: 'Disques de frein',



            all: 'Toutes les catégories',
            oilChange: 'Vidange d\'huile',
            filter: 'Filtre',
            oilFilter: 'Filtre à huile',
            fuelFilter: 'Filtre à carburant',
            cabinFilter: 'Filtre d\'habitacle',
            transmissionFilter: 'Filtre de transmission',
            airFilter: 'Filtre à air',
            carInspection: 'Contrôle technique',
            maintenance: 'Entretien',
            insurance: 'Assurance',
            repairs: 'Réparations',
            other: 'Autre'
        },
        settings: 'Paramètres',
        preferences: 'Préférences',
        currency: 'Devise',
        language: 'Langue',
        guestUser: 'Utilisateur invité',
        email: 'guest@cartrack.app',
        manageAccount: 'Gérer le compte',
        signOut: 'Se déconnecter',
        close: 'Fermer',
        currencies: { usd: 'Dollar américain', eur: 'Euro', gbp: 'Livre sterling' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Avant / Arrière',
        front: 'Avant',
        back: 'Arrière',
        frontAndBack: 'Avant et Arrière',
        placeholders: { expenseTitle: 'ex. Vidange moteur', amount: '0.00' },
        ..._ek,
    },

    ar: {
        appName: 'CarTrack',
        dashboard: 'لوحة التحكم',
        welcomeBack: 'مرحباً بعودتك. إليك نظرة عامة على نفقاتك.',
        changeCar: 'تغيير السيارة',
        editCarName: 'تعديل اسم السيارة',
        expensesBreakdown: 'تفاصيل المصروفات',
        noExpensesData: 'لا توجد بيانات مصروفات متاحة للإحصاء.',
        expenseLog: 'سجل المصروفات',
        manageExpenses: 'إدارة تكاليف سيارتك',
        totalSpent: 'إجمالي الإنفاق',
        filterByCategory: 'تصفية حسب الفئة',
        addNewExpense: 'إضافة مصروف جديد',
        noExpenses: 'لم يتم تسجيل أي مصروفات بعد.',
        newExpense: 'مصروف جديد',
        title: 'العنوان',
        amount: 'المبلغ',
        date: 'التاريخ',
        category: 'الفئة',
        filterType: 'نوع الفلتر',
        expiryDate: 'صالح حتى',
        cancel: 'إلغاء',
        saveExpense: 'حفظ المصروف',
        categories: {
            brakes: 'الفرامل',
            brakePads: 'تيل الفرامل',
            brakeRotors: 'أقراص الفرامل',



            all: 'جميع الفئات',
            oilChange: 'تغيير الزيت',
            filter: 'فلتر',
            oilFilter: 'فلتر الزيت',
            fuelFilter: 'فلتر الوقود',
            cabinFilter: 'فلتر المقصورة',
            transmissionFilter: 'فلتر ناقل الحركة',
            airFilter: 'فلتر الهواء',
            carInspection: 'فحص السيارة',
            maintenance: 'الصيانة',
            insurance: 'التأمين',
            repairs: 'الإصلاحات',
            other: 'أخرى'
        },
        settings: 'الإعدادات',
        preferences: 'التفضيلات',
        currency: 'العملة',
        language: 'اللغة',
        guestUser: 'مستخدم ضيف',
        email: 'guest@cartrack.app',
        manageAccount: 'إدارة الحساب',
        signOut: 'تسجيل الخروج',
        close: 'إغلاق',
        currencies: { usd: 'الدولار الأمريكي', eur: 'اليورو', gbp: 'الجنيه الإسترليني' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'أمامي / خلفي',
        front: 'أمامي',
        back: 'خلفي',
        frontAndBack: 'أمامي وخلفي',
        placeholders: { expenseTitle: 'مثال: تغيير زيت المحرك', amount: '0.00' },
        ..._ek,
    },

    pt: {
        appName: 'CarTrack',
        dashboard: 'Painel',
        welcomeBack: 'Bem-vindo de volta. Aqui está o seu resumo de despesas.',
        changeCar: 'Mudar carro',
        editCarName: 'Editar nome do carro',
        expensesBreakdown: 'Detalhe de despesas',
        noExpensesData: 'Sem dados de despesas disponíveis para estatísticas.',
        expenseLog: 'Registo de despesas',
        manageExpenses: 'Gerencie os custos relacionados com o seu carro',
        totalSpent: 'Total gasto',
        filterByCategory: 'Filtrar por categoria',
        addNewExpense: 'Adicionar nova despesa',
        noExpenses: 'Nenhuma despesa registada ainda.',
        newExpense: 'Nova despesa',
        title: 'Título',
        amount: 'Valor',
        date: 'Data',
        category: 'Categoria',
        filterType: 'Tipo de filtro',
        expiryDate: 'Válido até',
        cancel: 'Cancelar',
        saveExpense: 'Guardar despesa',
        categories: {
            brakes: 'Travões',
            brakePads: 'Pastilhas de travão',
            brakeRotors: 'Discos de travão',



            all: 'Todas as categorias',
            oilChange: 'Troca de óleo',
            filter: 'Filtro',
            oilFilter: 'Filtro de óleo',
            fuelFilter: 'Filtro de combustível',
            cabinFilter: 'Filtro de habitáculo',
            transmissionFilter: 'Filtro de transmissão',
            airFilter: 'Filtro de ar',
            carInspection: 'Inspeção do veículo',
            maintenance: 'Manutenção',
            insurance: 'Seguro',
            repairs: 'Reparações',
            other: 'Outro'
        },
        settings: 'Definições',
        preferences: 'Preferências',
        currency: 'Moeda',
        language: 'Idioma',
        guestUser: 'Utilizador convidado',
        email: 'guest@cartrack.app',
        manageAccount: 'Gerir conta',
        signOut: 'Terminar sessão',
        close: 'Fechar',
        currencies: { usd: 'Dólar americano', eur: 'Euro', gbp: 'Libra esterlina' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Dianteiro / Traseiro',
        front: 'Dianteiro',
        back: 'Traseiro',
        frontAndBack: 'Dianteiro e Traseiro',
        placeholders: { expenseTitle: 'ex. Troca de óleo do motor', amount: '0.00' },
        ..._ek,
    },

    ru: {
        appName: 'CarTrack',
        dashboard: 'Панель',
        welcomeBack: 'С возвращением. Вот обзор ваших расходов.',
        changeCar: 'Сменить автомобиль',
        editCarName: 'Изменить название автомобиля',
        expensesBreakdown: 'Разбивка расходов',
        noExpensesData: 'Нет данных о расходах для статистики.',
        expenseLog: 'Журнал расходов',
        manageExpenses: 'Управляйте расходами на автомобиль',
        totalSpent: 'Всего потрачено',
        filterByCategory: 'Фильтр по категории',
        addNewExpense: 'Добавить расход',
        noExpenses: 'Расходы ещё не записаны.',
        newExpense: 'Новый расход',
        title: 'Название',
        amount: 'Сумма',
        date: 'Дата',
        category: 'Категория',
        filterType: 'Тип фильтра',
        expiryDate: 'Действителен до',
        cancel: 'Отмена',
        saveExpense: 'Сохранить расход',
        categories: {
            brakes: 'Тормоза',
            brakePads: 'Тормозные колодки',
            brakeRotors: 'Тормозные диски',



            all: 'Все категории',
            oilChange: 'Замена масла',
            filter: 'Фильтр',
            oilFilter: 'Масляный фильтр',
            fuelFilter: 'Топливный фильтр',
            cabinFilter: 'Салонный фильтр',
            transmissionFilter: 'Фильтр трансмиссии',
            airFilter: 'Воздушный фильтр',
            carInspection: 'Технический осмотр',
            maintenance: 'Техобслуживание',
            insurance: 'Страховка',
            repairs: 'Ремонт',
            other: 'Другое'
        },
        settings: 'Настройки',
        preferences: 'Предпочтения',
        currency: 'Валюта',
        language: 'Язык',
        guestUser: 'Гость',
        email: 'guest@cartrack.app',
        manageAccount: 'Управление аккаунтом',
        signOut: 'Выйти',
        close: 'Закрыть',
        currencies: { usd: 'Доллар США', eur: 'Евро', gbp: 'Фунт стерлингов' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Передние / Задние',
        front: 'Передние',
        back: 'Задние',
        frontAndBack: 'Передние и Задние',
        placeholders: { expenseTitle: 'напр. Замена моторного масла', amount: '0.00' },
        ..._ek,
    },

    id: {
        appName: 'CarTrack',
        dashboard: 'Dasbor',
        welcomeBack: 'Selamat datang kembali. Berikut ringkasan pengeluaran Anda.',
        changeCar: 'Ganti Mobil',
        editCarName: 'Edit nama mobil',
        expensesBreakdown: 'Rincian Pengeluaran',
        noExpensesData: 'Tidak ada data pengeluaran untuk statistik.',
        expenseLog: 'Log Pengeluaran',
        manageExpenses: 'Kelola biaya terkait mobil Anda',
        totalSpent: 'Total Pengeluaran',
        filterByCategory: 'Filter berdasarkan Kategori',
        addNewExpense: 'Tambah Pengeluaran Baru',
        noExpenses: 'Belum ada pengeluaran yang dicatat.',
        newExpense: 'Pengeluaran Baru',
        title: 'Judul',
        amount: 'Jumlah',
        date: 'Tanggal',
        category: 'Kategori',
        filterType: 'Jenis Filter',
        expiryDate: 'Berlaku Hingga',
        cancel: 'Batal',
        saveExpense: 'Simpan Pengeluaran',
        categories: {
            brakes: 'Rem',
            brakePads: 'Kampas Rem',
            brakeRotors: 'Cakram Rem',



            all: 'Semua Kategori',
            oilChange: 'Ganti Oli',
            filter: 'Filter',
            oilFilter: 'Filter Oli',
            fuelFilter: 'Filter Bahan Bakar',
            cabinFilter: 'Filter Kabin',
            transmissionFilter: 'Filter Transmisi',
            airFilter: 'Filter Udara',
            carInspection: 'Inspeksi Kendaraan',
            maintenance: 'Perawatan',
            insurance: 'Asuransi',
            repairs: 'Perbaikan',
            other: 'Lainnya'
        },
        settings: 'Pengaturan',
        preferences: 'Preferensi',
        currency: 'Mata Uang',
        language: 'Bahasa',
        guestUser: 'Pengguna Tamu',
        email: 'guest@cartrack.app',
        manageAccount: 'Kelola Akun',
        signOut: 'Keluar',
        close: 'Tutup',
        currencies: { usd: 'Dolar AS', eur: 'Euro', gbp: 'Pound Inggris' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Depan / Belakang',
        front: 'Depan',
        back: 'Belakang',
        frontAndBack: 'Depan & Belakang',
        placeholders: { expenseTitle: 'mis. Ganti oli mesin', amount: '0.00' },
        ..._ek,
    },

    de: {
        appName: 'CarTrack',
        dashboard: 'Dashboard',
        welcomeBack: 'Willkommen zurück. Hier ist Ihre Ausgabenübersicht.',
        changeCar: 'Auto wechseln',
        editCarName: 'Autoname bearbeiten',
        expensesBreakdown: 'Ausgabenübersicht',
        noExpensesData: 'Keine Ausgabendaten für die Statistik verfügbar.',
        expenseLog: 'Ausgabenprotokoll',
        manageExpenses: 'Verwalten Sie Ihre fahrzeugbezogenen Kosten',
        totalSpent: 'Gesamt ausgegeben',
        filterByCategory: 'Nach Kategorie filtern',
        addNewExpense: 'Neue Ausgabe hinzufügen',
        noExpenses: 'Noch keine Ausgaben erfasst.',
        newExpense: 'Neue Ausgabe',
        title: 'Titel',
        amount: 'Betrag',
        date: 'Datum',
        category: 'Kategorie',
        filterType: 'Filtertyp',
        expiryDate: 'Gültig bis',
        cancel: 'Abbrechen',
        saveExpense: 'Ausgabe speichern',
        categories: {
            brakes: 'Bremsen',
            brakePads: 'Bremsbeläge',
            brakeRotors: 'Bremsscheiben',



            all: 'Alle Kategorien',
            oilChange: 'Ölwechsel',
            filter: 'Filter',
            oilFilter: 'Ölfilter',
            fuelFilter: 'Kraftstofffilter',
            cabinFilter: 'Innenraumfilter',
            transmissionFilter: 'Getriebefilter',
            airFilter: 'Luftfilter',
            carInspection: 'Fahrzeuginspektion',
            maintenance: 'Wartung',
            insurance: 'Versicherung',
            repairs: 'Reparaturen',
            other: 'Sonstiges'
        },
        settings: 'Einstellungen',
        preferences: 'Einstellungen',
        currency: 'Währung',
        language: 'Sprache',
        guestUser: 'Gastbenutzer',
        email: 'guest@cartrack.app',
        manageAccount: 'Konto verwalten',
        signOut: 'Abmelden',
        close: 'Schließen',
        currencies: { usd: 'US-Dollar', eur: 'Euro', gbp: 'Britisches Pfund' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Vorne / Hinten',
        front: 'Vorne',
        back: 'Hinten',
        frontAndBack: 'Vorne & Hinten',
        placeholders: { expenseTitle: 'z.B. Motorölwechsel', amount: '0.00' },
        ..._ek,
    },

    pl: {
        appName: 'CarTrack',
        dashboard: 'Panel',
        welcomeBack: 'Witaj z powrotem. Oto przegląd Twoich wydatków.',
        changeCar: 'Zmień samochód',
        editCarName: 'Edytuj nazwę samochodu',
        expensesBreakdown: 'Podział wydatków',
        noExpensesData: 'Brak danych o wydatkach do statystyk.',
        expenseLog: 'Rejestr wydatków',
        manageExpenses: 'Zarządzaj kosztami związanymi z samochodem',
        totalSpent: 'Łącznie wydano',
        filterByCategory: 'Filtruj według kategorii',
        addNewExpense: 'Dodaj nowy wydatek',
        noExpenses: 'Nie zapisano jeszcze żadnych wydatków.',
        newExpense: 'Nowy wydatek',
        title: 'Tytuł',
        amount: 'Kwota',
        date: 'Data',
        category: 'Kategoria',
        filterType: 'Typ filtra',
        expiryDate: 'Ważny do',
        cancel: 'Anuluj',
        saveExpense: 'Zapisz wydatek',
        categories: {
            brakes: 'Hamulce',
            brakePads: 'Klocki hamulcowe',
            brakeRotors: 'Tarcze hamulcowe',



            all: 'Wszystkie kategorie',
            oilChange: 'Wymiana oleju',
            filter: 'Filtr',
            oilFilter: 'Filtr oleju',
            fuelFilter: 'Filtr paliwa',
            cabinFilter: 'Filtr kabinowy',
            transmissionFilter: 'Filtr skrzyni biegów',
            airFilter: 'Filtr powietrza',
            carInspection: 'Przegląd techniczny',
            maintenance: 'Konserwacja',
            insurance: 'Ubezpieczenie',
            repairs: 'Naprawy',
            other: 'Inne'
        },
        settings: 'Ustawienia',
        preferences: 'Preferencje',
        currency: 'Waluta',
        language: 'Język',
        guestUser: 'Użytkownik gościnny',
        email: 'guest@cartrack.app',
        manageAccount: 'Zarządzaj kontem',
        signOut: 'Wyloguj się',
        close: 'Zamknij',
        currencies: { usd: 'Dolar amerykański', eur: 'Euro', gbp: 'Funt brytyjski' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Przód / Tył',
        front: 'Przód',
        back: 'Tył',
        frontAndBack: 'Przód i Tył',
        placeholders: { expenseTitle: 'np. Wymiana oleju silnikowego', amount: '0.00' },
        ..._ek,
    },

    sr: {
        appName: 'CarTrack',
        dashboard: 'Контролна табла',
        welcomeBack: 'Добродошли назад. Ево прегледа ваших трошкова.',
        changeCar: 'Промени ауто',
        editCarName: 'Измени назив аута',
        expensesBreakdown: 'Преглед трошкова',
        noExpensesData: 'Нема података о трошковима за статистику.',
        expenseLog: 'Евиденција трошкова',
        manageExpenses: 'Управљај трошковима свог аута',
        totalSpent: 'Укупно потрошено',
        filterByCategory: 'Филтрирај по категорији',
        addNewExpense: 'Додај нови трошак',
        noExpenses: 'Нема забележених трошкова.',
        newExpense: 'Нови трошак',
        title: 'Наслов',
        amount: 'Износ',
        date: 'Датум',
        category: 'Категорија',
        filterType: 'Тип филтера',
        expiryDate: 'Важи до',
        cancel: 'Откажи',
        saveExpense: 'Сачувај трошак',
        categories: {
            brakes: 'Кочнице',
            brakePads: 'Кочионе плочице',
            brakeRotors: 'Кочиони дискови',



            all: 'Све категорије',
            oilChange: 'Замена уља',
            filter: 'Филтер',
            oilFilter: 'Филтер уља',
            fuelFilter: 'Филтер горива',
            cabinFilter: 'Филтер кабине',
            transmissionFilter: 'Филтер мењача',
            airFilter: 'Ваздушни филтер',
            carInspection: 'Технички преглед',
            maintenance: 'Одржавање',
            insurance: 'Осигурање',
            repairs: 'Поправке',
            other: 'Остало'
        },
        settings: 'Подешавања',
        preferences: 'Поставке',
        currency: 'Валута',
        language: 'Језик',
        guestUser: 'Гост корисник',
        email: 'guest@cartrack.app',
        manageAccount: 'Управљај налогом',
        signOut: 'Одјави се',
        close: 'Затвори',
        currencies: { usd: 'Амерички долар', eur: 'Евро', gbp: 'Британска фунта' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Предње / Задње',
        front: 'Предње',
        back: 'Задње',
        frontAndBack: 'Предње и Задње',
        placeholders: { expenseTitle: 'нпр. Замена моторног уља', amount: '0.00' },
        ..._ek,
    },

    el: {
        appName: 'CarTrack',
        dashboard: 'Πίνακας',
        welcomeBack: 'Καλώς ήρθατε πίσω. Εδώ είναι η επισκόπηση εξόδων σας.',
        changeCar: 'Αλλαγή αυτοκινήτου',
        editCarName: 'Επεξεργασία ονόματος',
        expensesBreakdown: 'Ανάλυση εξόδων',
        noExpensesData: 'Δεν υπάρχουν δεδομένα εξόδων για στατιστικά.',
        expenseLog: 'Αρχείο εξόδων',
        manageExpenses: 'Διαχειριστείτε τα έξοδα του αυτοκινήτου σας',
        totalSpent: 'Σύνολο δαπανών',
        filterByCategory: 'Φιλτράρισμα κατά κατηγορία',
        addNewExpense: 'Προσθήκη νέου εξόδου',
        noExpenses: 'Δεν έχουν καταγραφεί έξοδα ακόμα.',
        newExpense: 'Νέο έξοδο',
        title: 'Τίτλος',
        amount: 'Ποσό',
        date: 'Ημερομηνία',
        category: 'Κατηγορία',
        filterType: 'Τύπος φίλτρου',
        expiryDate: 'Ισχύει έως',
        cancel: 'Ακύρωση',
        saveExpense: 'Αποθήκευση εξόδου',
        categories: {
            brakes: 'Φρένα',
            brakePads: 'Τακάκια φρένων',
            brakeRotors: 'Δισκόπλακες',



            all: 'Όλες οι κατηγορίες',
            oilChange: 'Αλλαγή λαδιού',
            filter: 'Φίλτρο',
            oilFilter: 'Φίλτρο λαδιού',
            fuelFilter: 'Φίλτρο καυσίμου',
            cabinFilter: 'Φίλτρο καμπίνας',
            transmissionFilter: 'Φίλτρο κιβωτίου',
            airFilter: 'Φίλτρο αέρα',
            carInspection: 'Τεχνικός έλεγχος',
            maintenance: 'Συντήρηση',
            insurance: 'Ασφάλεια',
            repairs: 'Επισκευές',
            other: 'Άλλο'
        },
        settings: 'Ρυθμίσεις',
        preferences: 'Προτιμήσεις',
        currency: 'Νόμισμα',
        language: 'Γλώσσα',
        guestUser: 'Επισκέπτης',
        email: 'guest@cartrack.app',
        manageAccount: 'Διαχείριση λογαριασμού',
        signOut: 'Αποσύνδεση',
        close: 'Κλείσιμο',
        currencies: { usd: 'Αμερικανικό δολάριο', eur: 'Ευρώ', gbp: 'Βρετανική λίρα' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Μπροστά / Πίσω',
        front: 'Μπροστά',
        back: 'Πίσω',
        frontAndBack: 'Μπροστά & Πίσω',
        placeholders: { expenseTitle: 'π.χ. Αλλαγή λαδιού κινητήρα', amount: '0.00' },
        ..._ek,
    },

    tr: {
        appName: 'CarTrack',
        dashboard: 'Gösterge Paneli',
        welcomeBack: 'Tekrar hoş geldiniz. İşte harcama özetiniz.',
        changeCar: 'Araba Değiştir',
        editCarName: 'Araba adını düzenle',
        expensesBreakdown: 'Harcama Dökümü',
        noExpensesData: 'İstatistikler için harcama verisi yok.',
        expenseLog: 'Harcama Günlüğü',
        manageExpenses: 'Arabanızla ilgili maliyetleri yönetin',
        totalSpent: 'Toplam Harcama',
        filterByCategory: 'Kategoriye Göre Filtrele',
        addNewExpense: 'Yeni Harcama Ekle',
        noExpenses: 'Henüz harcama kaydedilmedi.',
        newExpense: 'Yeni Harcama',
        title: 'Başlık',
        amount: 'Tutar',
        date: 'Tarih',
        category: 'Kategori',
        filterType: 'Filtre Tipi',
        expiryDate: 'Geçerlilik Tarihi',
        cancel: 'İptal',
        saveExpense: 'Harcamayı Kaydet',
        categories: {
            brakes: 'Frenler',
            brakePads: 'Fren Balataları',
            brakeRotors: 'Fren Diskleri',



            all: 'Tüm Kategoriler',
            oilChange: 'Yağ Değişimi',
            filter: 'Filtre',
            oilFilter: 'Yağ Filtresi',
            fuelFilter: 'Yakıt Filtresi',
            cabinFilter: 'Kabin Filtresi',
            transmissionFilter: 'Şanzıman Filtresi',
            airFilter: 'Hava Filtresi',
            carInspection: 'Araç Muayenesi',
            maintenance: 'Bakım',
            insurance: 'Sigorta',
            repairs: 'Onarımlar',
            other: 'Diğer'
        },
        settings: 'Ayarlar',
        preferences: 'Tercihler',
        currency: 'Para Birimi',
        language: 'Dil',
        guestUser: 'Misafir Kullanıcı',
        email: 'guest@cartrack.app',
        manageAccount: 'Hesabı Yönet',
        signOut: 'Çıkış Yap',
        close: 'Kapat',
        currencies: { usd: 'ABD Doları', eur: 'Euro', gbp: 'İngiliz Sterlini' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Ön / Arka',
        front: 'Ön',
        back: 'Arka',
        frontAndBack: 'Ön ve Arka',
        placeholders: { expenseTitle: 'örn. Motor yağı değişimi', amount: '0.00' },
        ..._ek,
    },

    no: {
        appName: 'CarTrack',
        dashboard: 'Oversikt',
        welcomeBack: 'Velkommen tilbake. Her er din utgiftsoversikt.',
        changeCar: 'Bytt bil',
        editCarName: 'Rediger bilnavn',
        expensesBreakdown: 'Utgiftsfordeling',
        noExpensesData: 'Ingen utgiftsdata tilgjengelig for statistikk.',
        expenseLog: 'Utgiftslogg',
        manageExpenses: 'Administrer bilrelaterte kostnader',
        totalSpent: 'Totalt brukt',
        filterByCategory: 'Filtrer etter kategori',
        addNewExpense: 'Legg til ny utgift',
        noExpenses: 'Ingen utgifter registrert ennå.',
        newExpense: 'Ny utgift',
        title: 'Tittel',
        amount: 'Beløp',
        date: 'Dato',
        category: 'Kategori',
        filterType: 'Filtertype',
        expiryDate: 'Gyldig til',
        cancel: 'Avbryt',
        saveExpense: 'Lagre utgift',
        categories: {
            brakes: 'Bremser',
            brakePads: 'Bremseklosser',
            brakeRotors: 'Bremseskiver',



            all: 'Alle kategorier',
            oilChange: 'Oljeskift',
            filter: 'Filter',
            oilFilter: 'Oljefilter',
            fuelFilter: 'Drivstoffilter',
            cabinFilter: 'Kupéfilter',
            transmissionFilter: 'Girkassefilter',
            airFilter: 'Luftfilter',
            carInspection: 'EU-kontroll',
            maintenance: 'Vedlikehold',
            insurance: 'Forsikring',
            repairs: 'Reparasjoner',
            other: 'Annet'
        },
        settings: 'Innstillinger',
        preferences: 'Preferanser',
        currency: 'Valuta',
        language: 'Språk',
        guestUser: 'Gjest',
        email: 'guest@cartrack.app',
        manageAccount: 'Administrer konto',
        signOut: 'Logg ut',
        close: 'Lukk',
        currencies: { usd: 'Amerikansk dollar', eur: 'Euro', gbp: 'Britisk pund' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Foran / Bak',
        front: 'Foran',
        back: 'Bak',
        frontAndBack: 'Foran & Bak',
        placeholders: { expenseTitle: 'f.eks. Motoroljeskift', amount: '0.00' },
        ..._ek,
    },

    sv: {
        appName: 'CarTrack',
        dashboard: 'Översikt',
        welcomeBack: 'Välkommen tillbaka. Här är din utgiftsöversikt.',
        changeCar: 'Byt bil',
        editCarName: 'Redigera bilnamn',
        expensesBreakdown: 'Utgiftsfördelning',
        noExpensesData: 'Inga utgiftsdata tillgängliga för statistik.',
        expenseLog: 'Utgiftslogg',
        manageExpenses: 'Hantera dina bilrelaterade kostnader',
        totalSpent: 'Totalt spenderat',
        filterByCategory: 'Filtrera efter kategori',
        addNewExpense: 'Lägg till ny utgift',
        noExpenses: 'Inga utgifter registrerade ännu.',
        newExpense: 'Ny utgift',
        title: 'Titel',
        amount: 'Belopp',
        date: 'Datum',
        category: 'Kategori',
        filterType: 'Filtertyp',
        expiryDate: 'Giltig till',
        cancel: 'Avbryt',
        saveExpense: 'Spara utgift',
        categories: {
            brakes: 'Bromsar',
            brakePads: 'Bromsbelägg',
            brakeRotors: 'Bromsskivor',



            all: 'Alla kategorier',
            oilChange: 'Oljebyte',
            filter: 'Filter',
            oilFilter: 'Oljefilter',
            fuelFilter: 'Bränslefilter',
            cabinFilter: 'Kupéfilter',
            transmissionFilter: 'Växellådsfilter',
            airFilter: 'Luftfilter',
            carInspection: 'Bilprovning',
            maintenance: 'Underhåll',
            insurance: 'Försäkring',
            repairs: 'Reparationer',
            other: 'Övrigt'
        },
        settings: 'Inställningar',
        preferences: 'Inställningar',
        currency: 'Valuta',
        language: 'Språk',
        guestUser: 'Gästanvändare',
        email: 'guest@cartrack.app',
        manageAccount: 'Hantera konto',
        signOut: 'Logga ut',
        close: 'Stäng',
        currencies: { usd: 'US-dollar', eur: 'Euro', gbp: 'Brittiskt pund' },
        languages: {
            en: 'English', bg: 'Български', zh: '中文', hi: 'हिन्दी',
            es: 'Español', fr: 'Français', ar: 'العربية', pt: 'Português',
            ru: 'Русский', id: 'Bahasa Indonesia', de: 'Deutsch', pl: 'Polski',
            sr: 'Српски', el: 'Ελληνικά', tr: 'Türkçe', no: 'Norsk', sv: 'Svenska'
        },
        brakePosition: 'Fram / Bak',
        front: 'Fram',
        back: 'Bak',
        frontAndBack: 'Fram & Bak',
        placeholders: { expenseTitle: 't.ex. Motoroljebyte', amount: '0.00' },
        ..._ek,
    }
};

// Helper function to get translation
export const getTranslation = (language, key) => {
    const keys = key.split('.');
    let result = translations[language] || translations.en;

    for (const k of keys) {
        result = result[k];
        if (!result) return key;
    }

    return result;
};
